import React, { createContext, ReactNode, useContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import { actions as studentActions } from '../redux/modules/student';
import config from '../config';
import { toast } from 'components/toast';

export const profilePhotoContext = createContext({} as ProfilePhotoContextData);

export default function ProfilePhotoProvider({ children }: ProfilePhotoProviderProps) {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  async function getImageBlob(imageUrl: HTMLImageElement) {
    const response = await fetch(imageUrl.src);
    return response.blob();
  }

  const uploadUserImage = async (
    croppedImage: HTMLImageElement,
    token: string,
    id_user: string,
    callBackFunction: () => void
  ) => {
    setLoading(true);
    try {
      const formData = new FormData();
      const blob = await getImageBlob(croppedImage as HTMLImageElement);
      const file = new File([blob], blob?.type, {
        type: blob?.type,
      });
      formData.append('photo', file as File);

      const response = await fetch(`${config.API_BASE}/v1/profile/photo`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        dispatch(studentActions.fetchStudentsById(id_user));
        callBackFunction();
        toast({
          message: 'Foto atualizada com sucesso',
          type: 'success',
        });
      } else {
        console.error('Error uploading image');
        toast({
          message: 'Erro ao atualizar foto',
          type: 'error',
        });
      }
    } catch (error) {
      console.error('Error uploading image', error);
      toast({
        message: 'Erro ao atualizar foto',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <profilePhotoContext.Provider
      value={{
        uploadUserImage,
        loading,
      }}
    >
      {children}
    </profilePhotoContext.Provider>
  );
}

export function useProfilePhoto() {
  const context = useContext(profilePhotoContext);
  if (!context) {
    throw new Error('useProfilePhoto must be used within a ProfilePhotoProvider');
  }
  return context;
}

type ProfilePhotoProviderProps = {
  children: ReactNode;
};

export type ProfilePhotoContextData = {
  uploadUserImage: (
    croppedImage: HTMLImageElement,
    token: string,
    id_user: string,
    callBackFunction: () => void
  ) => Promise<void>;
  loading: boolean;
};
